(() => {
  'use strict';

  const elementName = 'bennett-european-antiques-motion';

  if (customElements.get(elementName)) return;

  class BennettEuropeanAntiquesMotion extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.collectionLockup = this.querySelector('[data-ea-collection]');
      this.collectionLink = this.collectionLockup?.querySelector('a');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const searchParams = new URLSearchParams(window.location.search);
      this.forceReducedMotion = searchParams.get('motion') === 'reduce';
      this.isPaginatedView = Number.parseInt(searchParams.get('page') || '1', 10) > 1;

      if (this.isPaginatedView) this.classList.add('is-pagination-static');

      const enabled = this.dataset.motionEnabled === 'true';
      const hasGsap = Boolean(window.gsap && window.ScrollTrigger);

      if (!enabled || this.reducedMotion.matches || this.forceReducedMotion || this.isPaginatedView || !hasGsap) {
        this.showStaticCampaign();
        return;
      }

      this.initializeMotion();
    }

    disconnectedCallback() {
      this.media?.revert();
      this.media = null;
      this.timeline = null;
      this.initialized = false;
    }

    showStaticCampaign() {
      this.classList.remove('is-motion-ready');
      this.classList.add('is-static', 'is-ready');
      this.setCollectionInteractive(!this.isPaginatedView);
    }

    initializeMotion() {
      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      this.classList.remove('is-static');
      this.classList.add('is-motion-ready', 'is-ready');
      this.setCollectionInteractive(false);

      this.media = gsap.matchMedia();
      this.media.add(
        {
          desktop: '(min-width: 750px)',
          mobile: '(max-width: 749px)'
        },
        (context) => {
          const timeline = context.conditions.mobile
            ? this.createMobileTimeline(gsap, ScrollTrigger)
            : this.createDesktopTimeline(gsap, ScrollTrigger);

          this.timeline = timeline;

          return () => {
            this.setCollectionInteractive(false);
            timeline.scrollTrigger?.kill();
            timeline.kill();
            if (this.timeline === timeline) this.timeline = null;
          };
        }
      );

      requestAnimationFrame(() => ScrollTrigger.refresh());

      const pendingImages = [...this.querySelectorAll('img')].filter((image) => !image.complete);
      if (pendingImages.length) {
        Promise.all(
          pendingImages.map(
            (image) => new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            })
          )
        ).then(() => ScrollTrigger.refresh());
      }
    }

    getElements() {
      return {
        intro: this.querySelector('[data-ea-intro]'),
        european: this.querySelector('[data-ea-european]'),
        antiques: this.querySelector('[data-ea-antiques]'),
        hero: this.querySelector('[data-ea-hero]'),
        room: this.querySelector('[data-ea-room]'),
        detail: this.querySelector('[data-ea-detail]'),
        detailMedia: this.querySelector('.bennett-ea-motion__detail-media'),
        detailCopy: this.querySelector('[data-ea-detail-copy]'),
        galleryLeft: this.querySelector('[data-ea-gallery-left]'),
        galleryCenter: this.querySelector('[data-ea-gallery-center]'),
        galleryRight: this.querySelector('[data-ea-gallery-right]'),
        collection: this.collectionLockup
      };
    }

    prepareTimeline(gsap, elements, mobile = false) {
      gsap.set(elements.intro, { xPercent: -50, yPercent: -50 });
      gsap.set(elements.european, {
        xPercent: -50,
        yPercent: -50,
        autoAlpha: 0,
        x: '6vw',
        clipPath: 'inset(-24% 0% -32% 45%)'
      });
      gsap.set(elements.antiques, {
        xPercent: -50,
        yPercent: -50,
        autoAlpha: 0,
        x: '-6vw',
        clipPath: 'inset(-24% 45% -32% 0%)'
      });
      gsap.set(elements.hero, {
        xPercent: -50,
        yPercent: -50,
        autoAlpha: 0,
        x: 0,
        y: mobile ? '3svh' : '4vh',
        scale: 1
      });
      gsap.set(elements.detail, {
        left: mobile ? '0vw' : '58vw',
        width: mobile ? '100vw' : '42vw',
        xPercent: 100
      });
      gsap.set(elements.detailCopy, { autoAlpha: 0, y: mobile ? '1svh' : '2vh' });
      gsap.set([elements.galleryLeft, elements.galleryCenter, elements.galleryRight], {
        autoAlpha: 0,
        y: mobile ? '3svh' : '4vh',
        scale: 1
      });
      gsap.set(elements.collection, { xPercent: -50, autoAlpha: 0, y: mobile ? '1.5svh' : '2vh' });
      gsap.set(elements.room, { scale: 1 });
    }

    createScrollTimeline(gsap, scrub) {
      return gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: this,
          start: 'top top',
          end: 'bottom bottom',
          scrub,
          invalidateOnRefresh: true,
          onUpdate: (self) => this.setCollectionInteractive(self.progress >= 0.86)
        }
      });
    }

    createDesktopTimeline(gsap) {
      const elements = this.getElements();
      this.prepareTimeline(gsap, elements, false);
      const timeline = this.createScrollTimeline(gsap, 0.35);

      timeline
        .addLabel('invitation', 0)
        .to(elements.room, { scale: 1.003, duration: 8, ease: 'none' }, 0)
        .to(elements.intro, { autoAlpha: 0, y: '-2vh', duration: 3, ease: 'power2.out' }, 8)

        .addLabel('european', 11)
        .to(elements.hero, { autoAlpha: 1, y: 0, duration: 7, ease: 'power3.out' }, 11)
        .to(
          elements.european,
          { autoAlpha: 1, x: 0, clipPath: 'inset(-24% 0% -32% 0%)', duration: 7, ease: 'power4.out' },
          11
        )
        .to(
          elements.european,
          { autoAlpha: 0, x: '-5vw', clipPath: 'inset(-24% 100% -32% 0%)', duration: 4, ease: 'power4.inOut' },
          23
        )

        .addLabel('antiques', 26)
        .to(
          elements.antiques,
          { autoAlpha: 1, x: 0, clipPath: 'inset(-24% 0% -32% 0%)', duration: 9, ease: 'power4.out' },
          23
        )
        .to(elements.antiques, { autoAlpha: 0, y: '-2vh', duration: 3, ease: 'power3.out' }, 37)

        .addLabel('detail', 40)
        .to(elements.hero, { x: '-10vw', y: '1vh', duration: 9, ease: 'power3.out' }, 40)
        .to(elements.detail, { xPercent: 0, duration: 9, ease: 'power4.inOut' }, 40)
        .to(elements.detailCopy, { autoAlpha: 1, y: 0, duration: 5, ease: 'power2.out' }, 49)

        .addLabel('craft', 56)
        .to(elements.detail, { left: '28vw', width: '72vw', duration: 7, ease: 'power4.inOut' }, 56)
        .to(elements.hero, { x: '-14vw', duration: 7, ease: 'power3.out' }, 56)
        .to(elements.room, { scale: 1.008, duration: 12, ease: 'none' }, 56)
        .to(elements.galleryCenter, { autoAlpha: 0.34, y: '1.5vh', duration: 4, ease: 'power2.out' }, 68)
        .to(elements.hero, { autoAlpha: 0, duration: 3, ease: 'power2.out' }, 68)

        .addLabel('gallery', 70)
        .to(elements.detail, { xPercent: -145, duration: 12, ease: 'power4.inOut' }, 70)
        .to(elements.galleryCenter, { autoAlpha: 1, y: 0, duration: 5, ease: 'power3.out' }, 72)
        .to(elements.galleryLeft, { autoAlpha: 1, y: 0, duration: 6, ease: 'power3.out' }, 76)
        .to(elements.galleryRight, { autoAlpha: 1, y: 0, duration: 6, ease: 'power3.out' }, 80)
        .to(elements.room, { scale: 1.012, duration: 18, ease: 'none' }, 70)

        .addLabel('collection', 88)
        .to(elements.collection, { autoAlpha: 1, y: 0, duration: 6, ease: 'power3.out' }, 88)
        .to(elements.room, { scale: 1.012, duration: 6, ease: 'none' }, 94)
        .addLabel('timelineEnd', 100);

      return timeline;
    }

    createMobileTimeline(gsap) {
      const elements = this.getElements();
      this.prepareTimeline(gsap, elements, true);
      const timeline = this.createScrollTimeline(gsap, 0.22);

      timeline
        .addLabel('invitation', 0)
        .to(elements.room, { scale: 1.002, duration: 8, ease: 'none' }, 0)
        .to(elements.intro, { autoAlpha: 0, y: '-1.5svh', duration: 4, ease: 'power2.out' }, 8)

        .addLabel('european', 12)
        .to(elements.hero, { autoAlpha: 1, y: 0, duration: 8, ease: 'power3.out' }, 12)
        .to(
          elements.european,
          { autoAlpha: 1, x: 0, clipPath: 'inset(-24% 0% -32% 0%)', duration: 8, ease: 'power4.out' },
          12
        )
        .to(
          elements.european,
          { autoAlpha: 0, x: '-4vw', clipPath: 'inset(-24% 100% -32% 0%)', duration: 4, ease: 'power4.inOut' },
          23
        )

        .addLabel('antiques', 27)
        .to(
          elements.antiques,
          { autoAlpha: 1, x: 0, clipPath: 'inset(-24% 0% -32% 0%)', duration: 9, ease: 'power4.out' },
          23
        )
        .to(elements.antiques, { autoAlpha: 0, y: '-1.5svh', duration: 4, ease: 'power3.out' }, 37)

        .addLabel('detail', 41)
        .to(elements.detail, { xPercent: 0, duration: 10, ease: 'power4.inOut' }, 41)
        .to(elements.hero, { x: '-3vw', autoAlpha: 0.3, duration: 8, ease: 'power3.out' }, 43)
        .to(elements.detailCopy, { autoAlpha: 1, y: 0, duration: 5, ease: 'power2.out' }, 50)

        .addLabel('craft', 56)
        .to(elements.room, { scale: 1.005, duration: 12, ease: 'none' }, 56)
        .to(elements.galleryCenter, { autoAlpha: 0.28, y: '1svh', duration: 4, ease: 'power2.out' }, 68)
        .to(elements.hero, { autoAlpha: 0, duration: 3, ease: 'power2.out' }, 67)

        .addLabel('gallery', 70)
        .to(elements.detail, { xPercent: -108, duration: 11, ease: 'power4.inOut' }, 70)
        .to(elements.galleryCenter, { autoAlpha: 1, y: 0, duration: 6, ease: 'power3.out' }, 72)
        .to(elements.galleryLeft, { autoAlpha: 0.82, y: 0, duration: 5, ease: 'power3.out' }, 77)
        .to(elements.galleryRight, { autoAlpha: 0.82, y: 0, duration: 5, ease: 'power3.out' }, 81)

        .addLabel('collection', 88)
        .to(elements.collection, { autoAlpha: 1, y: 0, duration: 6, ease: 'power3.out' }, 88)
        .to(elements.room, { scale: 1.006, duration: 6, ease: 'none' }, 94)
        .addLabel('timelineEnd', 100);

      return timeline;
    }

    setCollectionInteractive(interactive) {
      if (!this.collectionLockup || !this.collectionLink) return;
      this.collectionLockup.setAttribute('aria-hidden', interactive ? 'false' : 'true');
      this.collectionLink.tabIndex = interactive ? 0 : -1;
    }
  }

  customElements.define(elementName, BennettEuropeanAntiquesMotion);
})();
